import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import logoLight from "../assets/logo-light.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Name", type: "text", testId: "contact-form-name", placeholder: "Your full name" },
  { name: "email", label: "Email", type: "email", testId: "contact-form-email", placeholder: "you@email.com" },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", project: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", email: "", project: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <section id="contact" className="relative bg-[#F4F1F5] bg-grid-light">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-32">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left: form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-[#c85a54] text-xs md:text-sm tracking-[0.3em] uppercase font-semibold">
              Let&apos;s Collaborate
            </span>
            <h2 className="mt-5 font-display font-black uppercase tracking-tight leading-[0.98] text-[#1A1518] text-3xl md:text-5xl text-balance">
              Have a project in mind? <span className="brand-gradient-text">Let&apos;s build it.</span>
            </h2>

            <form onSubmit={handleSubmit} className="mt-10 space-y-8" data-testid="contact-form">
              {FIELDS.map((f) => (
                <div key={f.name}>
                  <label className="block text-[11px] tracking-[0.25em] uppercase font-semibold text-[#4D454A] mb-2">
                    {f.label}
                  </label>
                  <input
                    data-testid={f.testId}
                    type={f.type}
                    name={f.name}
                    value={form[f.name]}
                    onChange={handleChange}
                    placeholder={f.placeholder}
                    className="w-full bg-transparent border-0 border-b-2 border-[#d8cfd8] focus:border-[#c85a54] outline-none py-3 text-[#1A1518] placeholder:text-[#b3a8b8] transition-colors duration-300"
                  />
                </div>
              ))}
              <div>
                <label className="block text-[11px] tracking-[0.25em] uppercase font-semibold text-[#4D454A] mb-2">
                  Project Requirement
                </label>
                <textarea
                  data-testid="contact-form-project"
                  name="project"
                  value={form.project}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Tell us about your vision..."
                  className="w-full bg-transparent border-0 border-b-2 border-[#d8cfd8] focus:border-[#c85a54] outline-none py-3 text-[#1A1518] placeholder:text-[#b3a8b8] transition-colors duration-300 resize-none"
                />
              </div>
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={loading}
                className="group rounded-full px-8 py-4 brand-gradient text-white hover:opacity-90 transition-opacity duration-300 uppercase tracking-widest text-xs font-bold flex items-center gap-2 disabled:opacity-60"
              >
                {loading ? "Sending..." : "Send Enquiry"}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </form>
          </motion.div>

          {/* Right: details */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:pl-12 lg:border-l border-[#E5DEE5]"
          >
            <h3 className="font-display font-black uppercase tracking-[0.2em] text-[#4a1c40] text-2xl md:text-3xl">
              Contact
            </h3>
            <div className="mt-10 space-y-8">
              <a
                href="mailto:foxel.architects@gmail.com"
                data-testid="contact-email-link"
                className="flex items-start gap-4 group"
              >
                <span className="w-11 h-11 rounded-xl bg-white border border-[#E5DEE5] flex items-center justify-center shrink-0 group-hover:brand-gradient transition-colors duration-300">
                  <Mail size={18} className="text-[#c85a54] group-hover:text-white transition-colors duration-300" />
                </span>
                <div>
                  <div className="text-[11px] tracking-[0.25em] uppercase text-[#8a828a] font-semibold">Email</div>
                  <div className="text-[#1A1518] text-base md:text-lg font-medium mt-1 break-all">foxel.architects@gmail.com</div>
                </div>
              </a>

              <a href="tel:+918097237982" data-testid="contact-phone-link" className="flex items-start gap-4 group">
                <span className="w-11 h-11 rounded-xl bg-white border border-[#E5DEE5] flex items-center justify-center shrink-0 group-hover:brand-gradient transition-colors duration-300">
                  <Phone size={18} className="text-[#c85a54] group-hover:text-white transition-colors duration-300" />
                </span>
                <div>
                  <div className="text-[11px] tracking-[0.25em] uppercase text-[#8a828a] font-semibold">Phone</div>
                  <div className="text-[#1A1518] text-base md:text-lg font-medium mt-1">+91 80972 37982</div>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-xl bg-white border border-[#E5DEE5] flex items-center justify-center shrink-0">
                  <MapPin size={18} className="text-[#c85a54]" />
                </span>
                <div>
                  <div className="text-[11px] tracking-[0.25em] uppercase text-[#8a828a] font-semibold">Location</div>
                  <div className="text-[#1A1518] text-base md:text-lg font-medium mt-1">Panvel, Navi Mumbai</div>
                </div>
              </div>
            </div>

            {/* Practising banner */}
            <div className="mt-10 rounded-full border border-[#E5DEE5] bg-white px-5 py-3 inline-flex items-center gap-3 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full brand-gradient" />
              <span className="text-[#4D454A] text-xs md:text-sm tracking-[0.2em] uppercase font-semibold">
                Practicing All Across India
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <footer className="header-gradient">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src={logoLight} alt="FOXEL Architects" className="h-10 w-auto object-contain" />
          <p className="text-white/70 text-[11px] md:text-xs tracking-[0.25em] uppercase text-center">
            © {year} FOXEL Architects — All Rights Reserved
          </p>
        </div>
      </footer>
    </section>
  );
};
